/**
 * UpgradePromptModal - Shows when user tries to access a feature not in their plan
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  LockOutlined as LockIcon,
  CheckCircleOutline as CheckIcon,
  Star as StarIcon,
  RocketLaunch as RocketIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useProfilePlan } from '../../contexts/ProfilePlanContext';
import { FEATURE_LABELS, PLAN_FEATURES } from '../../services/SubscriptionService';

const PLAN_DETAILS = {
  basic: {
    name: 'Basic',
    price: '₹999/month',
    color: '#10B981',
    icon: <CheckIcon />,
    highlights: ['Reply to Reviews', 'Create Posts', 'AI Generation', 'Free Website']
  },
  advance: {
    name: 'Advance',
    price: '₹1,999/month',
    color: '#3B82F6',
    icon: <StarIcon />,
    highlights: ['Everything in Basic', 'Heatmap Analysis', 'Competitor Analysis', 'Auto Reply']
  },
  pro: {
    name: 'Pro',
    price: '₹3,999/month',
    color: '#8B5CF6',
    icon: <RocketIcon />,
    highlights: ['Everything in Advance', 'Unlimited Posts', 'Priority Support', 'API Access']
  }
};

const UpgradePromptModal = () => {
  const navigate = useNavigate();
  const { showUpgradeModal, upgradeFeature, closeUpgradeModal, planCode } = useProfilePlan();

  if (!showUpgradeModal || !upgradeFeature) return null;

  const featureLabel = FEATURE_LABELS[upgradeFeature.name] || upgradeFeature.name;
  const requiredPlan = upgradeFeature.requiredPlan;
  const planDetails = PLAN_DETAILS[requiredPlan];
  const currentPlanLabel = PLAN_FEATURES[planCode]?.label || 'No Plan';

  const handleUpgrade = () => {
    closeUpgradeModal();
    navigate('/pricing');
  };

  return (
    <Dialog
      open={showUpgradeModal}
      onClose={closeUpgradeModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${planDetails?.color || '#667eea'} 0%, ${planDetails?.color || '#764ba2'}CC 100%)`,
          color: 'white',
          p: 3,
          textAlign: 'center'
        }}
      >
        <LockIcon sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
        <Typography variant="h5" fontWeight={700}>
          Feature Locked
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
          <strong>{featureLabel}</strong> requires a higher plan
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Current vs Required */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
          <Chip 
            label={`Current: ${currentPlanLabel}`} 
            variant="outlined" 
            color="default"
          />
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>→</Typography>
          <Chip 
            label={`Required: ${planDetails?.name}`} 
            sx={{ 
              bgcolor: planDetails?.color, 
              color: 'white',
              fontWeight: 600
            }} 
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Plan Highlights */}
        {planDetails && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Upgrade to {planDetails.name} to unlock:
            </Typography>
            <List dense>
              {planDetails.highlights.map((highlight, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckIcon sx={{ color: planDetails.color, fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={highlight}
                    primaryTypographyProps={{ fontSize: 14 }}
                  />
                </ListItem>
              ))}
            </List>
            
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: '#f5f5f5', 
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <Typography variant="body2" color="text.secondary">
                Starting at
              </Typography>
              <Typography variant="h5" fontWeight={700} color={planDetails.color}>
                {planDetails.price}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
        <Button 
          onClick={closeUpgradeModal}
          variant="outlined"
          color="inherit"
          sx={{ flex: 1 }}
        >
          Maybe Later
        </Button>
        <Button
          onClick={handleUpgrade}
          variant="contained"
          sx={{ 
            flex: 2,
            bgcolor: planDetails?.color || '#667eea',
            '&:hover': {
              bgcolor: planDetails?.color || '#5a6fd6',
              filter: 'brightness(0.9)'
            }
          }}
        >
          View Plans & Upgrade
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpgradePromptModal;
